import { useQuery } from "@tanstack/react-query";
import { SENSITIVE_DATA_UI_ENABLED } from "@/lib/features";
import { EIR_FILING_DEADLINE_DAYS } from "@shared/constants/jurisdiction-ontario";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { WITHHELD_GENERATED_FORM_TYPES, FILL_REVIEWED_REVIEW_COPY } from "@shared/constants/gov-form-framing";

// ── Official government form source URLs ──────────────────────────────────────
// Shown as a secondary "Download official blank form →" link on government forms.
// The executor uses our pre-filled working copy as reference while filling the real one.
// Presence in this map also marks a form as a Type-A government draft (banner shown).

const OFFICIAL_FORM_URLS: Partial<Record<string, string>> = {
  // Ontario Court — Rule 74 probate forms (Word format; page links to all downloads)
  "form-74a":    "https://ontariocourtforms.on.ca/en/rules-of-civil-procedure-forms/pre-formatted-fillable-estates-forms/",
  "form-74-1a":  "https://ontariocourtforms.on.ca/en/rules-of-civil-procedure-forms/pre-formatted-fillable-estates-forms/",
  "form-74b":    "https://ontariocourtforms.on.ca/en/rules-of-civil-procedure-forms/pre-formatted-fillable-estates-forms/",
  "form-74d":    "https://ontariocourtforms.on.ca/en/rules-of-civil-procedure-forms/pre-formatted-fillable-estates-forms/",
  // s134 (F-8 narrow): 74G — registry sourceUrl (the official .docx)
  "form-74g":    "https://ontariocourtforms.on.ca/static/media/uploads/courtforms/civil/74g/rcp-e-74g-1123.docx",
  // Ontario Ministry of Finance — Estate Information Return
  "ontario-estate-info-return": "https://www.fin.gov.on.ca/en/forms/",
  // Service Canada — CPP/OAS forms (direct PDF downloads)
  "cpp-death-benefit":    "https://catalogue.servicecanada.gc.ca/apps/EForms/pdf/en/ISP-1200.pdf",
  "cpp-oas-cancellation": "https://catalogue.servicecanada.gc.ca/apps/EForms/pdf/en/ISP-1201.pdf",
  "cpp-survivors-pension": "https://catalogue.servicecanada.gc.ca/apps/EForms/pdf/en/ISP-1300.pdf",
  // CRA — no standard fillable form; link to CRA guidance page
  "cra-notification": "https://www.canada.ca/en/revenue-agency/services/tax/individuals/life-events/what-to-do-death-taxpayer/notify-cra-death.html",
  // s134 (F-8 narrow): TX19 — registry sourceUrl (CRA forms page)
  "tx19": "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/tx19.html",
};

// ── Issuing authority per Type-A form (for draft notice) ─────────────────────

const ISSUING_AUTHORITY: Partial<Record<string, string>> = {
  "form-74a":               "the Ontario Superior Court of Justice",
  "form-74-1a":             "the Ontario Superior Court of Justice",
  "form-74b":               "the Ontario Superior Court of Justice",
  "form-74d":               "the Ontario Superior Court of Justice",
  "form-74g":               "the Ontario Superior Court of Justice",
  "ontario-estate-info-return": "the Ontario Ministry of Finance",
  "cpp-death-benefit":      "Service Canada",
  "cpp-oas-cancellation":   "Service Canada",
  "cpp-survivors-pension":  "Service Canada",
  "cra-notification":       "the Canada Revenue Agency",
  "tx19":                   "the Canada Revenue Agency",
};

// ── Types ──────────────────────────────────────────────────────────────────────

type DocumentType = string;
type DocumentUrgency = "immediate" | "30days" | "90days" | "180days" | "tools";

interface DocumentMeta {
  type: DocumentType;
  name: string;
  formNumber: string | null;
  urgency: DocumentUrgency;
  filledFields: number;
  totalFields: number;
  show: boolean;
}

interface EstateDocumentsAvailable {
  documents: DocumentMeta[];
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface EstateDocumentsPageProps {
  clientId: string;
}

// ── Pre-fill status badge ─────────────────────────────────────────────────────

function PrefillBadge({ filled, total }: { filled: number; total: number }) {
  if (total === 0) return <Badge variant="outline" className="text-xs">Template</Badge>;
  const pct = Math.round((filled / total) * 100);
  if (pct >= 80) {
    return (
      <Badge className="text-xs bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
        {filled}/{total} fields ready
      </Badge>
    );
  }
  if (pct >= 50) {
    return (
      <Badge className="text-xs bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">
        {filled}/{total} fields ready
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-muted-foreground">
      {filled}/{total} fields ready
    </Badge>
  );
}

// ── Single document row ───────────────────────────────────────────────────────

function DocumentRow({ doc, clientId }: { doc: DocumentMeta; clientId: string }) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(
        `/api/clients/${clientId}/estate-documents/generate/${doc.type}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Download failed" }));
        throw new Error(err.message ?? "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const name = `${doc.name.replace(/[^a-zA-Z0-9\s]/g, "").trim().replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "PDF downloaded", description: doc.name });
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  const officialUrl = OFFICIAL_FORM_URLS[doc.type];
  const isGovDraft = !!officialUrl;
  const authority = ISSUING_AUTHORITY[doc.type];
  // F-3b (D-132.9): we withhold our generated recreation of the court/tax fill_reviewed forms —
  // the official blank form + lawyer-review copy are shown instead, until the court-forms build lands.
  const isWithheld = doc.type in WITHHELD_GENERATED_FORM_TYPES;

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-start gap-3 min-w-0">
        <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium leading-snug">{doc.name}</p>
            {isGovDraft && !isWithheld && (
              <span
                title="This is a generated draft. The official government form will be available soon — verify all details before filing."
                style={{ display: "inline-block", backgroundColor: "#FEF3C7", color: "#C8862E", border: "1px solid #F6CC72", borderRadius: 4, fontSize: 11, fontWeight: 600, padding: "1px 6px", cursor: "default", whiteSpace: "nowrap" }}
              >
                Draft
              </span>
            )}
          </div>
          {doc.formNumber && (
            <p className="text-xs text-muted-foreground mt-0.5">Form {doc.formNumber}</p>
          )}
          {officialUrl && (
            <a
              href={officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
            >
              {isWithheld ? "View the official form" : "Download official blank form"}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {isWithheld ? (
            <p className="text-xs italic mt-1" style={{ color: "#C8862E" }} data-testid={`court-form-review-${doc.type}`}>
              {FILL_REVIEWED_REVIEW_COPY}
            </p>
          ) : isGovDraft && authority && (
            <p className="text-xs italic mt-1" style={{ color: "#C8862E" }}>
              Draft version — verify all details before filing with {authority}.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        {!isWithheld && <PrefillBadge filled={doc.filledFields} total={doc.totalFields} />}
        {!isWithheld && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
            className="gap-1.5"
          >
            {downloading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {downloading ? "Generating…" : "Download PDF"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Section group ─────────────────────────────────────────────────────────────

function DocumentSection({
  title,
  subtitle,
  docs,
  clientId,
}: {
  title: string;
  subtitle: string;
  docs: DocumentMeta[];
  clientId: string;
}) {
  if (docs.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="pt-0">
        {docs.map(doc => (
          <DocumentRow key={doc.type} doc={doc} clientId={clientId} />
        ))}
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EstateDocumentsPage({ clientId }: EstateDocumentsPageProps) {
  const { data, isLoading, error } = useQuery<EstateDocumentsAvailable>({
    queryKey: [`/api/clients/${clientId}/estate-documents/available`],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/estate-documents/available`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load documents");
      return res.json();
    },
    staleTime: 60_000,
    enabled: !!clientId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <p className="text-sm text-destructive">Failed to load estate documents. Please refresh.</p>
      </div>
    );
  }

  const visible = data.documents.filter(d => d.show);
  const byUrgency = (urgency: DocumentUrgency) => visible.filter(d => d.urgency === urgency);

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold">Estate Documents</h1>
        <p className="text-muted-foreground mt-1">
          Pre-filled forms and documents for estate settlement. Fields highlighted in yellow require manual completion.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex gap-3 p-4 rounded-lg border border-orange-200 bg-orange-50">
        <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
        <div className="text-sm text-orange-900">
          <p className="font-semibold mb-1">Before filing any document</p>
          <p>
            Review all information carefully. Fields with yellow highlighting require manual completion{SENSITIVE_DATA_UI_ENABLED ? " (SIN, account numbers, and other sensitive information are never stored in this system)" : ""}. These documents are pre-filled working copies —
            use them as reference while completing the official form. Government forms show a{" "}
            <span className="font-medium">Download official blank form</span> link to the source.
            This does not constitute legal advice.
          </p>
        </div>
      </div>

      {/* Immediate */}
      <DocumentSection
        title="Immediate — First 2 Weeks"
        subtitle="File these as soon as possible to stop payments and initiate benefits"
        docs={byUrgency("immediate")}
        clientId={clientId}
      />

      {/* Within 30 days */}
      <DocumentSection
        title="Within 30 Days"
        subtitle="Probate application and estate legal documents"
        docs={byUrgency("30days")}
        clientId={clientId}
      />

      {/* Within 90 days */}
      <DocumentSection
        title="Within 90 Days"
        subtitle="Service cancellations and survivor benefits"
        docs={byUrgency("90days")}
        clientId={clientId}
      />

      {/* Within 180 days */}
      <DocumentSection
        title={`Within ${EIR_FILING_DEADLINE_DAYS} Days`}
        subtitle="Ministry of Finance reporting requirement after probate"
        docs={byUrgency("180days")}
        clientId={clientId}
      />

      {/* Executor tools */}
      <DocumentSection
        title="Executor Tools"
        subtitle="Working documents to help manage estate administration"
        docs={byUrgency("tools")}
        clientId={clientId}
      />
    </div>
  );
}
